import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { api, can, getUser } from '../lib/api';
import {
  Card, Empty, ErrorNote, Field, Hash, Loading, Modal, PageHead, Pill, Stat, useResource, useToast,
} from '../components/ui';
import { dateTime, day } from '../lib/format';
import { Attachments } from '../components/Attachments';
import { RevealedField, useApprovalAware, useReveal } from '../components/Controls';
import {
  ErasureConfirmations, OVERRIDE_MIN, ProofForm, ProofView, emptyProof, proofBody, proofComplete, useErasure, type ProofDraft,
} from '../components/ErasurePanel';

/**
 * Data Principal rights requests (DPDP ss.11–14), filed from the preference
 * centre and handled here. Every request has a due date; overdue requests
 * raise a signal.
 */

type Kind = 'access' | 'correction' | 'erasure' | 'nomination' | 'nomination_activation' | 'grievance';

interface Request {
  id: string;
  reference: string;
  kind: Kind;
  status: 'open' | 'acknowledged' | 'completed' | 'rejected';
  details: Record<string, unknown>;
  /** Nomination only. Stored encrypted; the API opens it only inside a reveal window. */
  nominee?: { name: string; contact: string };
  /** Nomination only, when not revealed. */
  nomineeMasked?: { name: string; contact: string };
  receivedAt: string;
  dueBy: string;
  overdue: boolean;
  acknowledgedAt: string | null;
  acknowledgedBy: string | null;
  closedAt: string | null;
  closedBy: string | null;
  outcome: string | null;
  principalHash: string;
}

/** Written from the notice's retention statements when an erasure is completed. */
interface ErasureResponse {
  text: string;
  erasedFrom: string[];
  retainedIn: Array<{ system: string; period: string; until: string | null; because: string }>;
  fromStatements: boolean;
}

const KIND_LABEL: Record<Kind, string> = {
  access: 'Access (S.11)',
  correction: 'Correction (S.12)',
  erasure: 'Erasure (S.12)',
  nomination: 'Nomination (legacy)',
  nomination_activation: 'Nominee activation (S.14)',
  grievance: 'Grievance (S.13)',
};

const STATUS_TONE: Record<Request['status'], 'ok' | 'warn' | 'bad' | undefined> = {
  open: 'warn', acknowledged: undefined, completed: 'ok', rejected: 'bad',
};

/** Kinds the list can filter by. Nominee activations are filtered on the Nominations tab. */
const FILTER_KINDS: Kind[] = ['access', 'correction', 'erasure', 'grievance', 'nomination'];

export default function RequestsPage() {
  const user = getUser();
  const navigate = useNavigate();
  const [status, setStatus] = useState<'open' | 'closed' | 'all'>('open');
  const [kind, setKind] = useState<Kind | ''>('');
  const [open, setOpen] = useState<Request | null>(null);
  const [reload, setReload] = useState(0);

  const data = useResource<{ requests: Request[]; counts: { open: number; overdue: number; unacknowledged: number } }>(
    () => api.get(`/v1/requests?status=${status}${kind ? `&kind=${kind}` : ''}`), [status, kind, reload],
  );
  const rows = data.data?.requests ?? [];
  const counts = data.data?.counts;

  // ?ref=REQ-… (from Today) opens that request.
  const [params, setParams] = useSearchParams();
  const ref = params.get('ref');
  useEffect(() => {
    if (!ref || !data.data) return;
    const hit = data.data.requests.find((x) => x.reference === ref);
    if (hit) { setOpen(hit); setParams({}, { replace: true }); } else if (status !== 'all') setStatus('all');
  }, [ref, data.data]);

  return (
    <>
      <PageHead title="Rights requests" />

      <ErrorNote error={data.error} />

      <div className="tabs">
        {(['open', 'closed', 'all'] as const).map((t) => (
          <button key={t} className={status === t ? 'active' : ''} onClick={() => setStatus(t)}>
            {t === 'open' ? 'Open' : t === 'closed' ? 'Closed' : 'All'}
            {t === 'open' && counts?.open ? <span className="tab-count">{counts.open}</span> : null}
          </button>
        ))}
        <select value={kind} onChange={(e) => setKind(e.target.value as Kind | '')} style={{ width: 'auto', marginLeft: 12 }}>
          <option value="">All kinds</option>
          {FILTER_KINDS.map((k) => <option key={k} value={k}>{KIND_LABEL[k]}</option>)}
        </select>
      </div>

      <Card>
        {data.loading && !data.data && <Loading what="Loading requests" />}
        {data.data && rows.length === 0 && <Empty>No requests.</Empty>}
        {rows.length > 0 && (
          <div className="table-scroll">
            <table className="data">
              <thead>
                <tr>
                  <th>Reference</th><th>Kind</th><th>Principal</th><th>Received</th><th>Due</th><th>Status</th><th>Handled by</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} onClick={() => (r.kind === 'nomination_activation' ? navigate(`/nominations?ref=${encodeURIComponent(r.reference)}`) : setOpen(r))} style={{ cursor: 'pointer' }}>
                    <td>
                      {r.kind === 'nomination_activation'
                        ? <Link to={`/nominations?ref=${encodeURIComponent(r.reference)}`} onClick={(e) => e.stopPropagation()}><strong>{r.reference}</strong></Link>
                        : <strong>{r.reference}</strong>}
                    </td>
                    <td>{KIND_LABEL[r.kind] ?? r.kind}</td>
                    <td><Link to={`/principals/${r.principalHash}`} onClick={(e) => e.stopPropagation()}>Record {r.principalHash.slice(0, 10)}…</Link></td>
                    <td className="small">{day(r.receivedAt)}</td>
                    <td className="small">
                      {day(r.dueBy)}{r.overdue && <> <Pill tone="bad">Overdue</Pill></>}
                    </td>
                    <td><Pill tone={STATUS_TONE[r.status]}>{r.status.charAt(0).toUpperCase() + r.status.slice(1)}</Pill></td>
                    <td className="small">{r.closedBy ?? r.acknowledgedBy ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {open && (
        <RequestModal
          request={open}
          canHandle={can(user, 'requests:handle')}
          onClose={() => setOpen(null)}
          onChanged={() => { setOpen(null); setReload((n) => n + 1); }}
        />
      )}
    </>
  );
}

function RequestModal({ request: r, canHandle, onClose, onChanged }: {
  request: Request; canHandle: boolean; onClose: () => void; onChanged: () => void;
}) {
  const toast = useToast();
  const approvalAware = useApprovalAware();
  const reveal = useReveal('request', r.id);
  const [outcome, setOutcome] = useState('');
  const [busy, setBusy] = useState(false);
  const isOpen = r.status === 'open' || r.status === 'acknowledged';
  const erasure = r.kind === 'erasure';
  const [preview, setPreview] = useState<ErasureResponse | null>(null);
  const tasks = useErasure(r.id, erasure);
  const [proof, setProof] = useState<ProofDraft>(emptyProof());
  const [override, setOverride] = useState('');
  const outstanding = tasks.data?.outstanding ?? 0;
  const erasureReady = proofComplete(proof) && (outstanding === 0 || override.trim().length >= OVERRIDE_MIN);

  // An erasure is answered from the notice's retention statements; show the
  // text before it is sent so the operator knows what the person will read.
  useEffect(() => {
    if (!erasure || !isOpen) return;
    api.get<{ response: ErasureResponse }>(`/v1/requests/${r.id}/erasure-response`)
      .then((res) => {
        setPreview(res.response);
        // The proof starts from the systems the notice names.
        setProof((p) => (p.systems ? p : { ...p, systems: res.response.erasedFrom.join(', ') }));
      })
      .catch(() => setPreview(null));
  }, [r.id, erasure, isOpen]);

  async function act(path: string, body?: unknown, message = 'Updated.') {
    setBusy(true);
    try {
      approvalAware(await api.post(path, body), message);
      onChanged();
    } catch (err) {
      toast((err as Error).message, 'bad');
    } finally {
      setBusy(false);
    }
  }

  const d = r.details;
  const summary = d.summary as { purposes?: Array<{ purpose: string; dataCategories: string[]; decision: string; live: boolean; noticeVersion: number | null }> } | undefined;
  const response = (d.erasureResponse as ErasureResponse | undefined) ?? null;
  const canComplete = erasure ? erasureReady : outcome.trim().length >= 5;
  const erasureProof = (d.erasureProof as Record<string, unknown> | undefined) ?? null;

  return (
    <Modal
      wide
      title={`${r.reference} · ${KIND_LABEL[r.kind] ?? r.kind}`}
      onClose={onClose}
      footer={
        <>
          <button onClick={onClose}>Close</button>
          {canHandle && r.status === 'open' && (
            <button onClick={() => act(`/v1/requests/${r.id}/acknowledge`, undefined, 'Acknowledged.')} disabled={busy}>Acknowledge</button>
          )}
          {canHandle && isOpen && (
            <>
              <button className="danger" disabled={busy || outcome.trim().length < 5}
                onClick={() => act(`/v1/requests/${r.id}/close`, { status: 'rejected', outcome }, 'Rejected.')}>
                Reject
              </button>
              <button className="primary" disabled={busy || !canComplete}
                onClick={() => act(`/v1/requests/${r.id}/close`, {
                  status: 'completed', outcome,
                  ...(erasure ? { proof: proofBody(proof), ...(outstanding > 0 ? { overrideReason: override.trim() } : {}) } : {}),
                },
                  erasure ? 'Completed. Live consents withdrawn, processors told, response recorded.' : 'Completed.')}>
                {erasure ? 'Complete and withdraw consents' : 'Complete'}
              </button>
            </>
          )}
        </>
      }
    >
      <dl className="kv">
        <dt>Principal</dt><dd><Link to={`/principals/${r.principalHash}`}>Open record</Link> · <Hash value={r.principalHash} head={16} /></dd>
        <dt>Contact</dt><dd><RevealedField reveal={reveal} value={reveal.values?.identifier} />{reveal.dialog}</dd>
        <dt>Received</dt><dd>{dateTime(r.receivedAt)}</dd>
        <dt>Due</dt><dd>{r.dueBy}{r.overdue && <> <Pill tone="bad">Overdue</Pill></>}</dd>
        <dt>Status</dt><dd>{r.status}{r.acknowledgedBy && ` · acknowledged by ${r.acknowledgedBy}`}{r.closedBy && ` · closed by ${r.closedBy}`}</dd>
        {r.kind === 'correction' && <>
          <dt>Field</dt><dd>{String(d.field ?? '')}</dd>
          <dt>Requested value</dt><dd>{String(d.requestedValue ?? '')}</dd>
          {d.note ? <><dt>Note</dt><dd>{String(d.note)}</dd></> : null}
        </>}
        {r.kind === 'erasure' && <><dt>Reason</dt><dd>{String(d.reason ?? '—')}</dd></>}
        {r.kind === 'nomination' && <>
          <dt>Nominee</dt><dd>{reveal.values?.nomineeName ?? r.nominee?.name ?? <span className="mono muted">{r.nomineeMasked?.name ?? 'Hidden'}</span>}</dd>
          <dt>Nominee contact</dt><dd>{r.nominee && !reveal.values ? r.nominee.contact : <RevealedField reveal={reveal} value={reveal.values?.nomineeContact} masked={r.nomineeMasked?.contact} />}</dd>
          <dt>Relationship</dt><dd>{String(d.relationship ?? '—')}</dd>
        </>}
        {r.kind === 'grievance' && <>
          <dt>Category</dt><dd>{String(d.category ?? '').replace(/_/g, ' ')}</dd>
          <dt>Description</dt><dd style={{ whiteSpace: 'pre-line' }}>{String(d.description ?? '')}</dd>
          {d.category === 'unwanted_tracking' && (
            <><dt>Complaint check</dt><dd>
              <Link to={`/cookies?view=complaint&reference=${r.principalHash.slice(0, 12)}&on=${r.receivedAt.slice(0, 10)}&request=${encodeURIComponent(r.reference)}${Array.isArray(d.cookies) ? `&cookies=${encodeURIComponent((d.cookies as string[]).join(','))}` : ''}&text=${encodeURIComponent(String(d.description ?? '').slice(0, 500))}`}>
                Open the record beside the scans
              </Link>
            </dd></>
          )}
        </>}
        {r.outcome && <><dt>Outcome</dt><dd style={{ whiteSpace: 'pre-line' }}>{r.outcome}</dd></>}
        {Array.isArray(d.withdrawnPurposeIds) && (
          <><dt>Consents withdrawn</dt><dd>{(d.withdrawnPurposeIds as string[]).length}</dd></>
        )}
      </dl>

      {summary?.purposes && (
        <>
          <h2 className="section">Summary provided to the principal (S.11)</h2>
          <table className="data">
            <thead><tr><th>Purpose</th><th>Data</th><th>Decision</th><th>Notice</th></tr></thead>
            <tbody>
              {summary.purposes.map((p) => (
                <tr key={p.purpose}>
                  <td>{p.purpose}</td>
                  <td className="small">{p.dataCategories.join(', ')}</td>
                  <td>{p.decision}</td>
                  <td>{p.noticeVersion ? `v${p.noticeVersion}` : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      {erasure && (response || (isOpen && preview)) && (
        <>
          <h2 className="section">{response ? 'Response given to the person' : 'Response the person will receive'}</h2>
          <ResponseBlock response={(response ?? preview)!} />
        </>
      )}

      {erasure && tasks.data && (
        <>
          <h2 className="section">Processor confirmations</h2>
          <ErasureConfirmations requestId={r.id} status={tasks.data} canHandle={canHandle && isOpen} onChanged={tasks.reload} />
        </>
      )}

      {erasure && erasureProof && (
        <>
          <h2 className="section">Proof of erasure</h2>
          <ProofView proof={erasureProof} />
        </>
      )}

      {erasure && canHandle && isOpen && (
        <>
          <h2 className="section">Proof of erasure</h2>
          <ProofForm value={proof} onChange={setProof} outstanding={outstanding} override={override} onOverride={setOverride} />
        </>
      )}

      <h2 className="section">Evidence files</h2>
      <Attachments entityType="request" entityId={r.id} canAdd={canHandle} />

      {canHandle && isOpen && (
        <Field
          label={erasure ? 'Note to the person' : 'Outcome'}
          help={erasure
            ? 'Optional on completion; added after the generated response. Required to reject. Recorded in the audit log.'
            : 'Required to complete or reject. Recorded in the audit log.'}
        >
          <textarea rows={3} value={outcome} onChange={(e) => setOutcome(e.target.value)} />
        </Field>
      )}
    </Modal>
  );
}

/** The erasure response as the person reads it, with the retained systems as a table. */
function ResponseBlock({ response }: { response: ErasureResponse }) {
  return (
    <>
      <p style={{ marginTop: 0 }}>{response.text}</p>
      {!response.fromStatements && (
        <div className="note warn">No published notice carries a retention statement. Add systems of record under the notice's "Where the data is kept".</div>
      )}
      {response.retainedIn.length > 0 && (
        <table className="data">
          <thead><tr><th>Retained in</th><th>Until</th><th>Because</th></tr></thead>
          <tbody>
            {response.retainedIn.map((x) => (
              <tr key={x.system}>
                <td>{x.system}</td>
                <td className="small">{x.until ? day(x.until) : x.period}</td>
                <td className="small">{x.because}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </>
  );
}
